import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { useCandidate } from './CandidateProvider'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'

// dummy translation function (replace with i18n when we do translations)
const t = (key, opts = {}) => opts?.default || key

// Helper to format duration as "HH:MM"
function formatDuration(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0')
  const m = String(minutes % 60).padStart(2, '0')
  return `${h}:${m}`
}

const useCandidateSummary = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/coaching_summary.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching } = useQuery({
    queryKey: ['coaching_summary', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving coaching summary:', error)
    return null
  }

  const { groupedCoachNotes = [], coachNoteTopicCounts = [] } = data || {}
  return {
    groupedCoachNotes,
    coachNoteTopicCounts,
    isFetching,
  }
}

const NoteDetails = ({ note }) => {
  return (
    <>
      {note.services.map((service, i) => (
        <span key={Object.keys(service)[0]}>
          <strong>
            <u>
              {t(
                `partner_admin.candidates.coach_notes.form.${Object.keys(service)[0].replace(/\s+/g, '_').toLowerCase()}`,
                { default: Object.keys(service)[0] },
              )}
            </u>
          </strong>
          {' - '}
          {Object.values(service)
            .flat()
            .map((subitem, idx) => (
              <span
                className="bs-tip"
                title={new Date(subitem.created_at).toLocaleDateString()}
                key={idx}
              >
                {subitem.topic.map((topic, tIdx) => (
                  <span key={tIdx}>
                    {t(
                      `partner_admin.candidates.coach_notes.form.${topic.replace(/\s+/g, '_').toLowerCase()}`,
                      { default: topic },
                    )}
                    {tIdx < subitem.topic.length - 1 && ', '}
                  </span>
                ))}
                {idx < subitem.length - 1 && ', '}
              </span>
            ))
            .reduce((acc, el, idx, arr) => {
              acc.push(el)
              if (idx < arr.length - 1) acc.push(', ')
              return acc
            }, [])}
          {i < note.services.length - 1 && <span>,&nbsp;&nbsp;&nbsp;</span>}
        </span>
      ))}
    </>
  )
}

const GroupedCoachNotesByType = () => {
  const { groupedCoachNotes } = useCandidateSummary()

  return (
    <div className="overflow-x-auto rounded-2xl border border-silver bg-white">
      <Table className="min-w-full" tableClass="min-w-full">
        <TableHeader>
          <TableRow className="bg-intoo-blue-medium text-lg text-white hover:bg-intoo-blue-medium">
            <TableHead className="font-medium text-white">
              {t('partner_admin.candidates.coach_notes.index.type_of_service', {
                default: 'Type of Service',
              })}
            </TableHead>
            <TableHead className="font-medium text-white">
              {t('partner_admin.candidates.coach_notes.index.number_of_notes', {
                default: 'No. of Service Notes',
              })}
            </TableHead>
            <TableHead className="font-medium text-white">
              {t(
                'partner_admin.candidates.coach_notes.index.number_of_services',
                { default: 'No. of Services Delivered' },
              )}
            </TableHead>
            <TableHead className="font-medium text-white">
              {t(
                'partner_admin.candidates.coach_notes.index.detail_of_services',
                { default: 'Detail of Services Delivered' },
              )}
            </TableHead>
            <TableHead className="font-medium text-white">
              {t(
                'partner_admin.candidates.coach_notes.index.total_service_time',
                { default: 'Total Service Time' },
              )}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedCoachNotes && groupedCoachNotes.length > 0 ? (
            groupedCoachNotes.map((note) => (
              <TableRow
                key={note.note_type}
                className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]"
              >
                <TableCell>
                  {t(
                    `partner_admin.candidates.coach_notes.form.${note.note_type}`,
                    {
                      default:
                        note.note_type.charAt(0).toUpperCase() +
                        note.note_type.slice(1),
                    },
                  )}
                </TableCell>
                <TableCell>{note.notes_count}</TableCell>
                <TableCell>{note.topics_count}</TableCell>
                <TableCell>
                  <NoteDetails note={note} />
                </TableCell>
                <TableCell>{formatDuration(note.total_duration)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5}>
                <p className="alert alert-warning">
                  {t('partner_admin.candidates.coach_notes.index.no_services', {
                    default:
                      'No coach services have been recorded for this candidate.',
                  })}
                </p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

// Helper to split categories into 3 columns based on breakpoints
function splitCategories(categories) {
  const col1 = []
  const col2 = []
  const col3 = []
  let currentCol = col1
  categories.forEach(([category, topics]) => {
    if (category === 'Negotiation') currentCol = col2
    if (category === 'Outreach') currentCol = col3
    currentCol.push([category, topics])
  })
  return [col1, col2, col3]
}

const CoachNotesTopicCounts = () => {
  const { coachNoteTopicCounts } = useCandidateSummary()

  if (!coachNoteTopicCounts) return null

  // Convert to array of [category, topics] and split into columns
  const [col1, col2, col3] = splitCategories(coachNoteTopicCounts)

  const renderColumn = (col, idx) => (
    <div key={idx} className="w-full px-2 md:w-1/3">
      {col.map(([category, topics]) => (
        <div key={category} className="p-2 pl-[75px]">
          <h3 className="mb-2 text-lg font-semibold">{category}</h3>
          <ul className="space-y-1 text-gray-800">
            {Object.entries(topics || {}).map(([label, count]) => (
              <li key={label} className="flex items-center">
                <span className="mr-2 flex h-5 w-5 items-center justify-center rounded-sm border border-gray-600 text-sm font-medium">
                  {count}
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )

  return (
    <div className="my-10 flex flex-col justify-center md:flex-row">
      {renderColumn(col1, 1)}
      {renderColumn(col2, 2)}
      {renderColumn(col3, 3)}
    </div>
  )
}

const CoachNotesSummarySection = () => {
  return (
    <>
      <GroupedCoachNotesByType />
      <CoachNotesTopicCounts />
    </>
  )
}

export default CoachNotesSummarySection
